import { Component, OnInit, ViewChild } from '@angular/core';

import {DomSanitizer} from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ECMService } from '../services/ecm.service';
import { AppConfig } from 'src/assets/config/config/app-config';
import { PDFDocument, PDFName } from 'pdf-lib';
import { HttpClient, HttpHeaders } from '@angular/common/http';

declare var $: any;

@Component({
  selector: 'app-public',
  templateUrl: './public-user.component.html',
  styleUrls: ['./public-user.component.css']
})
export class PublicUserComponent implements OnInit {

  fileUrl: any;
  apiUrl: string;
  documentId: string;
  fileType: string;
  supportedFileTypes;
  syncfusionSupportedFileType;
  sanitizer: any;
  guestUsername: string;
  showDownloadMessage: boolean = false;
  downloadUrl: string = '';
  isIE: boolean = false;

  constructor(route: ActivatedRoute, sanitizer: DomSanitizer, public router: Router, public ecmService: ECMService, private config: AppConfig, private http: HttpClient) {
    this.documentId = route.snapshot.queryParams['documentId'];
    this.fileType = route.snapshot.queryParams['fileType'];
    this.supportedFileTypes = this.config.getConfig("downloadFileType");
    this.syncfusionSupportedFileType = this.config.getConfig("syncfusionSupportedFileType");
    this.apiUrl = this.config.getConfig("serviceurl");
    this.sanitizer = sanitizer;
    this.guestUsername = this.config.getConfig("guestUsername");
    this.isIE = this.detectIE();
  }

  detectIE(): boolean {
    // Only genuine IE (MSIE/Trident) needs the download-only fallback; Edge can render PDFs inline.
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    const trident = ua.indexOf('Trident/');
    return (msie > 0 || trident > 0);
  }

  ngOnInit(): void {
    this.guestDocumentProcess();
  }

  async guestDocumentProcess() {
    let isAccess = true;   
    let httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': this.config.getConfig("authtoken")
        })           
    };
       
    let resp = await this.http
      .get<any>(this.apiUrl + `/documents/get/` + this.documentId, httpOptions).toPromise();
    if (resp !== "") {
      let properties = resp["metadata"]["properties"];
      if (properties["Public Search"] == false) {
        isAccess = false;
      }
    }
    if (isAccess) {
      if (this.supportedFileTypes.includes(this.fileType)) {
        this.showDownloadMessage = true;
      } else if (this.syncfusionSupportedFileType.includes(this.fileType)) {
        const docurl = this.config.getConfig("docurl");
        this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${docurl}?&docId=${this.documentId};${this.fileType};${this.guestUsername}`); 
      } else {
  
        if (this.fileType === "application/pdf") {
          this.verifyPDFFile(this.documentId,this.guestUsername);
        } else {
          this.viewBrowserDocument(this.documentId);
        }
        // this.viewBrowserDocument(this.documentId);
        // const docurl = this.config.getConfig("docurl");
        // this.fileUrl = sanitizer.bypassSecurityTrustResourceUrl(`${docurl}?&docId=${documentId};${fileType};this.guestUsername`);           
      }
      if (this.documentId && this.fileType) {
        localStorage.setItem('isDocLoad', 'true');
      } else {
        localStorage.setItem('isDocLoad', 'false');
      }
    } else {
      window.alert("User doesn't have permission to view this document");
    }
  }

  async verifyPDFFile(docId: string, userName: string) {
    let resp = await this.ecmService.shareDocument(docId, userName).toPromise();
    if (resp !== "") {
      var url = resp.slice(5);
      
      // For IE, skip portfolio check and use direct viewing or download
      if (this.isIE) {
        // IE has poor native PDF support, provide download option
        this.showDownloadMessage = true;
        this.downloadUrl = url;
        window.alert("Internet Explorer has limited PDF viewing support. Please use the download button to view the document in Adobe Reader.");
      } else {
        // Malformed PDFs (broken xref entries) can make pdf-lib's recovery parser very slow on IE/Edge; fail open after a timeout.
        var result = await this.withTimeout(this.isPdfPortfolio(url), 5000, false);
        if (result) {
          window.alert("Viewer doesn't support PDF Portfolio file, Please download and view it with Adobe Reader");
        } else {
          // Add parameters to prevent edit mode and optimize viewing
          // view=FitH - Fit to width
          // toolbar=0 - Hide toolbar (reduces edit options)
          // navpanes=0 - Hide navigation panes
          // scrollbar=1 - Keep scrollbar
          this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url + '#view=FitH&zoom=80&toolbar=0&navpanes=0&scrollbar=1');
        }
      }               
    }
  }

  withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
    const timeout = new Promise<T>(resolve => setTimeout(() => resolve(fallback), ms));
    return Promise.race([promise, timeout]);
  }

  async isPdfPortfolio(pdfFile: string) {
    try {
      // Load the PDF document using HttpClient for better browser compatibility
      const existingPdfBytes = await this.http.get(pdfFile, { responseType: 'arraybuffer' }).toPromise();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
    
      // Access the PDF catalog
      const catalog = pdfDoc.catalog;
    
      // Check for portfolio-specific entries in the catalog
      const isPortfolio = catalog.has(PDFName.of('Collection'));
    
      return isPortfolio;
    } catch (error) {
      console.error('Error checking PDF portfolio:', error);
      return false; // Default to false if check fails
    }
  }

  async hasFillableFormFields(pdfFile: string) {
    try {
      const existingPdfBytes = await this.http.get(pdfFile, { responseType: 'arraybuffer' }).toPromise();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const form = pdfDoc.getForm();
      const fields = form.getFields();
      return fields.length > 0;
    } catch (error) {
      console.error('Error checking form fields:', error);
      return false;
    }
  }

  async isPasswordProtected(pdfFile: string) {
    try {
      const pdfBytes = await this.http.get(pdfFile, { responseType: 'arraybuffer' }).toPromise();
      await PDFDocument.load(pdfBytes);
      return false; // No password
    } catch (error: any) {
      if (error && error.message && error.message.includes('password')) {
        return true; // Password protected
      } else {
        return true; // Other error occurred
      }
    }
  }

  viewDocument(docId: string) {
    this.ecmService.shareDocument(docId, this.guestUsername)
        .subscribe(
            resp => {
                if (resp !== "") {
                  var url = resp.slice(5);
                  let newWindow = window.open();
                  if (newWindow) {
                    newWindow.location = url as any;
                    setTimeout(function() {
                      if (newWindow) {
                        newWindow.close();
                      }                    
                    }, 3000);
                  }                  
                }
            }
        ); 
  }

  viewBrowserDocument(docId: string) {
    this.ecmService.shareDocument(docId, this.guestUsername)
        .subscribe(
            resp => {
              if (resp !== "") {
                var url = resp.slice(5);
                // Add view parameters to prevent edit mode and display in iframe
                this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url + '#view=FitH&zoom=80&toolbar=0&navpanes=0&scrollbar=1');
              }
            }
        ); 
  }
  
  getDownloadUrl(docId: string) {
    return new Promise((resolve, reject) => {
      this.ecmService.viewDocument(docId, this.guestUsername)
          .subscribe(
              resp => {
                  if (resp !== "") {
                    var url = resp.slice(5);
                    this.downloadUrl = url;  
                    resolve(url);              
                  } else {
                    reject('Empty response');
                  }
                }
        );
    });
  }

  downloadFile() {
    this.getDownloadUrl(this.documentId)
    .then(url => {
      window.open(this.downloadUrl, '_blank');
    });
  }
}



